import { z } from 'zod';
/**
 * Машиночитаемые коды ошибок платформы (ТЗ §6.1).
 *
 * Код — часть публичного контракта: фронтенд ветвится по нему, а не по тексту
 * сообщения. Добавлять коды можно, менять и переиспользовать существующие — нет.
 */
export const ErrorCode = {
    // — Общие ————————————————————————————————————————————————
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    NOT_FOUND: 'NOT_FOUND',
    CONFLICT: 'CONFLICT',
    RATE_LIMITED: 'RATE_LIMITED',
    SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
    // — Аутентификация и доступ ——————————————————————————————
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    TOKEN_INVALID: 'TOKEN_INVALID',
    REFRESH_TOKEN_REUSED: 'REFRESH_TOKEN_REUSED',
    USER_INACTIVE: 'USER_INACTIVE',
    PERMISSION_DENIED: 'PERMISSION_DENIED',
    // — Ресурсы ————————————————————————————————————————————
    ORGANIZATION_NOT_FOUND: 'ORGANIZATION_NOT_FOUND',
    USER_NOT_FOUND: 'USER_NOT_FOUND',
    SITE_NOT_FOUND: 'SITE_NOT_FOUND',
    NODE_NOT_FOUND: 'NODE_NOT_FOUND',
    MONITOR_NOT_FOUND: 'MONITOR_NOT_FOUND',
    CREDENTIAL_NOT_FOUND: 'CREDENTIAL_NOT_FOUND',
    CAMERA_NOT_FOUND: 'CAMERA_NOT_FOUND',
    RECORDER_NOT_FOUND: 'RECORDER_NOT_FOUND',
    SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
    // — Агенты и туннель ——————————————————————————————————
    AGENT_NOT_FOUND: 'AGENT_NOT_FOUND',
    AGENT_OFFLINE: 'AGENT_OFFLINE',
    ENROLLMENT_TOKEN_INVALID: 'ENROLLMENT_TOKEN_INVALID',
    ENROLLMENT_TOKEN_USED: 'ENROLLMENT_TOKEN_USED',
    TUNNEL_STREAM_FAILED: 'TUNNEL_STREAM_FAILED',
    TUNNEL_WINDOW_EXHAUSTED: 'TUNNEL_WINDOW_EXHAUSTED',
    // — VNC (ТЗ §5.1, задача 8: у каждой причины отказа свой код) ————
    VNC_TICKET_INVALID: 'VNC_TICKET_INVALID',
    VNC_PORT_CLOSED: 'VNC_PORT_CLOSED',
    VNC_HOST_UNREACHABLE: 'VNC_HOST_UNREACHABLE',
    VNC_TIMEOUT: 'VNC_TIMEOUT',
    VNC_PROTOCOL_ERROR: 'VNC_PROTOCOL_ERROR',
    VNC_VERSION_UNSUPPORTED: 'VNC_VERSION_UNSUPPORTED',
    VNC_SECURITY_TYPE_UNSUPPORTED: 'VNC_SECURITY_TYPE_UNSUPPORTED',
    VNC_AUTH_FAILED: 'VNC_AUTH_FAILED',
    VNC_NO_CREDENTIAL: 'VNC_NO_CREDENTIAL',
    VNC_SERVER_CLOSED: 'VNC_SERVER_CLOSED',
    VNC_SESSION_LIMIT_REACHED: 'VNC_SESSION_LIMIT_REACHED',
    // — SSH (фаза 9b; у каждой причины отказа свой код, как у VNC) ——
    SSH_TICKET_INVALID: 'SSH_TICKET_INVALID',
    SSH_PORT_CLOSED: 'SSH_PORT_CLOSED',
    SSH_HOST_UNREACHABLE: 'SSH_HOST_UNREACHABLE',
    SSH_TIMEOUT: 'SSH_TIMEOUT',
    SSH_PROTOCOL_ERROR: 'SSH_PROTOCOL_ERROR',
    /**
     * Ключ хоста отличается от закреплённого.
     *
     * Отдельный код, а не разновидность отказа в подключении: интерфейс
     * по нему показывает сравнение отпечатков и предложение подтвердить
     * смену, а не просто сообщение об ошибке (ADR 026).
     */
    SSH_HOST_KEY_CHANGED: 'SSH_HOST_KEY_CHANGED',
    SSH_HOST_KEY_UNTRUSTED: 'SSH_HOST_KEY_UNTRUSTED',
    SSH_AUTH_FAILED: 'SSH_AUTH_FAILED',
    /** Способ аутентификации узла сервером не предлагается. */
    SSH_AUTH_METHOD_UNSUPPORTED: 'SSH_AUTH_METHOD_UNSUPPORTED',
    SSH_NO_CREDENTIAL: 'SSH_NO_CREDENTIAL',
    /** Приватный ключ не разобрался или требует парольной фразы, которой нет. */
    SSH_KEY_INVALID: 'SSH_KEY_INVALID',
    /**
     * Общего алгоритма обмена ключами, шифра или типа ключа хоста нет.
     *
     * Обычно означает оборудование старше набора по умолчанию: сообщение
     * называет алгоритм и предлагает включить устаревшие на узле.
     */
    SSH_NO_MATCHING_ALGORITHM: 'SSH_NO_MATCHING_ALGORITHM',
    SSH_SERVER_CLOSED: 'SSH_SERVER_CLOSED',
    SSH_SESSION_LIMIT_REACHED: 'SSH_SESSION_LIMIT_REACHED',
    /** Сервер не объявил subsystem `sftp` — файловой панели у узла нет. */
    SSH_SFTP_UNSUPPORTED: 'SSH_SFTP_UNSUPPORTED',
    // — Передача файлов TightVNC ————————————————————————————
    FILE_TRANSFER_UNSUPPORTED: 'FILE_TRANSFER_UNSUPPORTED',
    FILE_NOT_FOUND: 'FILE_NOT_FOUND',
    FILE_ACCESS_DENIED: 'FILE_ACCESS_DENIED',
    FILE_PATH_INVALID: 'FILE_PATH_INVALID',
    FILE_TOO_LARGE: 'FILE_TOO_LARGE',
    FILE_TRANSFER_FAILED: 'FILE_TRANSFER_FAILED',
    // — Видео ——————————————————————————————————————————————
    CAMERA_PROBE_FAILED: 'CAMERA_PROBE_FAILED',
    CAMERA_STREAM_FAILED: 'CAMERA_STREAM_FAILED',
    /** Тикет на поток недействителен, просрочен или уже использован (§11, фаза 7, задача 5). */
    STREAM_TICKET_INVALID: 'STREAM_TICKET_INVALID',
    /** go2rtc не отвечает или ответил не тем, чего ждали. */
    MEDIA_SERVER_UNAVAILABLE: 'MEDIA_SERVER_UNAVAILABLE',
    CODEC_UNSUPPORTED: 'CODEC_UNSUPPORTED',
    ARCHIVE_UNSUPPORTED: 'ARCHIVE_UNSUPPORTED',
    ARCHIVE_UNAVAILABLE: 'ARCHIVE_UNAVAILABLE',
    STREAM_LIMIT_REACHED: 'STREAM_LIMIT_REACHED',
    /** API регистратора не ответило или ответило не тем, что можно разобрать. */
    RECORDER_DETECT_FAILED: 'RECORDER_DETECT_FAILED',
    /**
     * Камера — канал регистратора: её создаёт, правит и удаляет только
     * определение каналов (ADR 037), а не маршруты камеры.
     */
    CAMERA_MANAGED_BY_RECORDER: 'CAMERA_MANAGED_BY_RECORDER',
    // — Секреты ————————————————————————————————————————————
    MASTER_KEY_MISSING: 'MASTER_KEY_MISSING',
    DECRYPTION_FAILED: 'DECRYPTION_FAILED',
};
/** Тело ошибки в едином формате ТЗ §8.1. */
export const errorBodySchema = z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
});
export const errorResponseSchema = z.object({
    error: errorBodySchema,
});
/**
 * Базовый класс ошибок домена. HTTP-статус — свойство ошибки, а не результат
 * догадки в контроллере: так один и тот же случай везде отдаётся одинаково.
 *
 * **Про `message`.** С фазы 10 это поле для разработчика, а не для
 * пользователя: оно уходит в журналы, в трассировки и тем, кто зовёт api
 * не из браузера, и написано поэтому по-английски и коротко. Человеку текст
 * собирает клиент — по `code` и `details`, на своём языке (§11, фаза 10,
 * задача 3). Иначе язык сообщения об отказе определялся бы языком сервера,
 * а не языком того, кто смотрит на экран.
 */
export class AppError extends Error {
    code;
    httpStatus;
    details;
    constructor(code, message, httpStatus, details) {
        super(message);
        this.name = new.target.name;
        this.code = code;
        this.httpStatus = httpStatus;
        this.details = details;
        Error.captureStackTrace?.(this, new.target);
    }
    toResponse() {
        return {
            error: {
                code: this.code,
                message: this.message,
                ...(this.details ? { details: this.details } : {}),
            },
        };
    }
}
/** 400 — тело или параметры запроса не прошли Zod-валидацию. */
export class ValidationError extends AppError {
    constructor(message = 'Request validation failed', details) {
        super(ErrorCode.VALIDATION_FAILED, message, 400, details);
    }
}
/** 401 — запрос без действительного access-токена. */
export class UnauthenticatedError extends AppError {
    constructor(message = 'Authentication required', code = ErrorCode.UNAUTHENTICATED, details) {
        super(code, message, 401, details);
    }
}
/** 403 — пользователь опознан, но прав на действие нет. */
export class ForbiddenError extends AppError {
    constructor(message = 'Permission denied', code = ErrorCode.PERMISSION_DENIED, details) {
        super(code, message, 403, details);
    }
}
/**
 * 404 — ресурса нет либо он принадлежит чужой организации.
 *
 * ТЗ §11, фаза 1: чужие ресурсы отдают именно 404, а не 403 — существование
 * ресурса в другой организации не раскрывается.
 */
export class NotFoundError extends AppError {
    constructor(message = 'Resource not found', code = ErrorCode.NOT_FOUND, details) {
        super(code, message, 404, details);
    }
}
/** 409 — конфликт состояния: дубль уникального поля, повторное действие. */
export class ConflictError extends AppError {
    constructor(message = 'Resource state conflict', code = ErrorCode.CONFLICT, details) {
        super(code, message, 409, details);
    }
}
/** 429 — сработал лимит частоты (ТЗ §10.4). */
export class RateLimitedError extends AppError {
    retryAfterSec;
    constructor(retryAfterSec, message = 'Rate limit exceeded') {
        super(ErrorCode.RATE_LIMITED, message, 429, { retryAfterSec });
        this.retryAfterSec = retryAfterSec;
    }
}
/** 503 — внешняя зависимость недоступна: агент, go2rtc, БД, Redis. */
export class ServiceUnavailableError extends AppError {
    constructor(message = 'Service unavailable', code = ErrorCode.SERVICE_UNAVAILABLE, details) {
        super(code, message, 503, details);
    }
}
/** 500 — непредвиденный сбой. Наружу отдаётся без подробностей. */
export class InternalError extends AppError {
    constructor(message = 'Internal server error', details) {
        super(ErrorCode.INTERNAL_ERROR, message, 500, details);
    }
}
export function isAppError(value) {
    return value instanceof AppError;
}
//# sourceMappingURL=errors.js.map